<script lang="ts">
  import {
    Button,
    DarkModeToggle,
    Divider,
    Select,
    type Language,
    type Theme,
  } from '@bruce/components'
  import { LANGUAGE_NAMES } from '$lib/constants/lang'
  import { STRINGS } from '$lib/constants/strings'
  import { type Writable } from 'svelte/store'
  import { utils } from '$lib/utils'
  import { page } from '$app/stores'

  export let lang: Language = 'en'
  export let src: string | undefined = undefined
  export let theme: Writable<Theme | null>
  export let storedTheme: Theme | undefined = undefined
  export let trial = false
  export let currentCity: string | undefined = undefined

  $: localeRoute = utils.getRoute(lang)
  let menuOpen = false

  function toggleMenu() {
    menuOpen = !menuOpen
  }

  $: routes = [
    { path: '/get-started', name: STRINGS[lang].APPBAR_GET_STARTED },
    {
      path: `/studios${currentCity ? `/${utils.toSpeakingUrl(currentCity)}` : ''}`,
      name: STRINGS[lang].APPBAR_STUDIOS,
    },
    { path: '/membership', name: STRINGS[lang].APPBAR_MEMBERSHIP },
    { path: '/credits', name: STRINGS[lang].APPBAR_CREDITS },
    { path: '/faq', name: STRINGS[lang].APPBAR_FAQ },
  ]
</script>

<nav>
  {#if src}
    <div class="logo">
      <a href={localeRoute('/')} title={STRINGS[lang].APPBAR_LOGO_TITLE}>
        <picture>
          {#if storedTheme || $theme}
            <source srcset={src} />
          {/if}
          <source
            srcset="/images/bruce-studios-logo-white.svg"
            media="(prefers-color-scheme: dark)"
          />
          <source
            srcset="/images/bruce-studios-logo-black.svg"
            media="(prefers-color-scheme: light)"
          />
          <img {src} alt={STRINGS[lang].APPBAR_LOGO_ALT} />
        </picture>
      </a>
    </div>
  {/if}
  <ul class="desktop">
    {#each routes as route (route.name)}
      <li><a href={localeRoute(route.path)}>{route.name}</a></li>
    {/each}
    {#if trial}
      <Divider orientation="vertical" />
      <Button rounded type="button" variant="flat" href={localeRoute('/trial')}>
        {STRINGS[lang].TRIAL_BUTTON_SHORT}
      </Button>
      <Divider orientation="vertical" />
    {/if}
  </ul>
  <div class="actions">
    <DarkModeToggle defaultTheme={storedTheme} bind:theme />
    <Select
      variant="outlined"
      rounded
      appendIcon="ph-globe"
      on:change={(event) => utils.changeLanguage(event, $page.url)}
      bind:value={lang}
    >
      {#each Object.entries(LANGUAGE_NAMES) as [key, value] (key)}
        <option value={key}>{value}</option>
      {/each}
    </Select>
  </div>
  <div class="mobile-actions">
    {#if trial}
      <Button rounded type="button" variant="flat" href={localeRoute('/trial')}>
        {STRINGS[lang].TRIAL_BUTTON_SHORT}
      </Button>
    {/if}
    <Button
      type="button"
      icon={menuOpen ? 'ph-x' : 'ph-list'}
      variant="text"
      rounded
      on:click={toggleMenu}
    />
  </div>
  <div role="menu" class="menu" class:menuOpen>
    <ul class="mobile">
      <li>
        <a href={localeRoute('/')} on:click={toggleMenu}
          >{STRINGS[lang].APPBAR_HOME}</a
        >
      </li>
      {#each routes as route (route.name)}
        <li><a href={route.path} on:click={toggleMenu}>{route.name}</a></li>
      {/each}
    </ul>
    <div class="menu-actions">
      <DarkModeToggle defaultTheme={storedTheme} bind:theme />
      <Select
        variant="outlined"
        rounded
        appendIcon="ph-globe"
        on:change={(event) => utils.changeLanguage(event, $page.url)}
        bind:value={lang}
      >
        {#each Object.entries(LANGUAGE_NAMES) as [key, value] (key)}
          <option value={key}>{value}</option>
        {/each}
      </Select>
    </div>
  </div>
</nav>

<style lang="scss">
  @use '@bruce/assets/css/breakpoints';

  nav {
    display: flex;
    align-items: center;
    justify-content: space-around;
    min-height: var(--appbar-height);
    background: var(--color-surface);
    gap: 1em;
    padding: 0 1em;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 12;
    box-sizing: border-box;

    @media screen and (max-width: breakpoints.$breakpoint-lg) {
      justify-content: space-between;
      gap: 0.5em;
    }

    .logo {
      a {
        display: flex;

        picture {
          height: 30px;

          @media screen and (max-width: breakpoints.$breakpoint-lg) {
            height: 20px;
          }

          img {
            height: 100%;
          }
        }
      }
    }

    ul {
      li {
        font-size: 18px;

        a {
          text-decoration: none;

          &:hover,
          &:focus {
            text-decoration: underline;
          }
        }
      }
    }

    ul.desktop {
      list-style-type: none;
      display: flex;
      align-items: center;
      gap: 2em;

      @media screen and (max-width: breakpoints.$breakpoint-lg) {
        display: none;
      }
    }

    .menu {
      display: none;
      @media screen and (max-width: breakpoints.$breakpoint-lg) {
        position: fixed;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 100%;
        height: calc(100% - var(--appbar-height));
        top: 0;
        left: 0;
        z-index: -1;
        transform: translate3d(0, -75%, 0) scaleY(0.25);
        background-color: var(--color-surface);
        margin: 0;
        padding-bottom: 2em;

        &.menuOpen {
          top: var(--appbar-height);
          transform: translateZ(0) scaleY(1);
          transition: transform 300ms ease;
        }
      }

      ul.mobile {
        display: flex;
        flex-direction: column;
        list-style-type: none;
        gap: 2em;

        li {
          font-size: 24px;
        }
      }

      .menu-actions {
        box-sizing: border-box;
        display: flex;
        gap: 1em;
        padding: 0 2em;
        width: 100%;

        :global(*:last-child) {
          flex: 1;
        }
      }
    }

    .actions {
      display: flex;
      align-items: center;
      gap: 2em;

      @media screen and (max-width: breakpoints.$breakpoint-lg) {
        display: none;
      }
    }

    .mobile-actions {
      display: none;

      @media screen and (max-width: breakpoints.$breakpoint-lg) {
        display: flex;
        align-items: center;
        gap: 1em;
      }
    }
  }
</style>
